import React, { useEffect, useRef, useMemo, useState, useContext } from 'react';
import { flushSync } from 'react-dom';

import { Stack, xcss, Box } from '@atlaskit/primitives';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import { fg } from '@confluence/feature-gating';
import { getCommentPositionTopOfSection } from '@confluence/comments-util';
import { ReactionsProvider } from '@confluence/comment-context';
import { ObjectCommentQueryParamsListener } from '@confluence/comments-query-param-handlers';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { usePageContentId } from '@confluence/page-context';
import {
	useCommentsDataState,
	useActiveCommentsQuery,
	CommentType,
	findCommentById,
	getTopLevelParentComment,
} from '@confluence/comments-data';
import {
	getAnnotationThreadKeys,
	getOpenCommentThreads,
	getVisibleCommentThreads,
	LocationValue,
	useCommentsPanelState,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';
import { useContentState } from '@confluence/content-state/entry-points/useContentState';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import { ContentAppearanceType } from '@confluence/content-appearance';
import { CommentsType } from '@confluence/comments-panel-queries';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';
import { useAnnotationsIsOffline } from '@confluence/annotation-provider-store';
import { useUnreadComments } from '@confluence/unread-comments';
import type { InlineCommentAuthorUser } from '@confluence/inline-comments-queries';
import {
	ExperienceTrackerContext,
	VIEW_PAGE_COMMENTS_EXPERIENCE,
} from '@confluence/experience-tracker';
import { PAGE_COMMENTS_METRIC } from '@confluence/comments-section/entry-points/pageSegmentLoadMetrics';

import { ObjectAddCommentContainer } from './ObjectAddCommentContainer';
import { ObjectCommentsList } from './ObjectCommentsList';
import { ObjectCommentHeader } from './ObjectCommentHeader';

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
	width: '100%',
});

const wrapperStyle = xcss({
	position: 'relative',
	visibility: 'visible',
	minHeight: '50px',
	margin: '0 auto',
	width: '100%',
});

const placeholderStyle = xcss({
	width: '60%',
});

const addCommentContainerStyles = xcss({
	transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	transformOrigin: 'top',
	position: 'sticky',
	bottom: 'space.0',
	zIndex: '1', // Required for create comment editor overflow to show on top of page content
});

const addCommentContainerAnimationHiddenStyles = xcss({
	transform: 'translateY(100%)',
	opacity: 0,
	pointerEvents: 'none',
});

const addCommentContainerAnimationShowStyles = xcss({
	transform: 'translateY(0)',
	opacity: 1,
	pointerEvents: 'auto',
});

type CommentsQueryHookVariables = {
	pageId: string;
	type: CommentsType;
	skip: boolean;
};

type ObjectCommentContainerProps = {
	pageWidthType?: string;
};

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

// DO NOT UPDATE THIS - It just exists to give us a quick revert in case updates to the real component go south
// This is just a copy of what's in master as of 5/28. Enough has changed with ordering & logic to make gating individual changes impractical.
// If you're seeing this after June 6, 2025 contact jhalbach
export const ObjectCommentLegacyContainerComponent = withFlags(
	({ flags, pageWidthType }: WithFlagsProps & ObjectCommentContainerProps) => {
		const [contentId] = usePageContentId();
		const wrapperRef = useRef<HTMLDivElement>(null);
		const [commentSectionTop, setCommentSectionTop] = useState<number | undefined>(undefined);
		const [isInViewport, setIsInViewport] = useState(false);
		const [allCommentsRendered, setAllCommentsRendered] = useState(false);
		const [hasLoaded, setHasLoaded] = useState(false);
		const [forceLoadComments, setForceLoadComments] = useState(false);
		const [state] = useContentState();
		const pageMode = useGetPageMode();
		const isServer = useRenderServerPlaceholder();
		const { orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap, hasNestedComments } =
			useCommentsDataState();
		const focusedCommentId = useFocusedCommentIdQueryParam();
		const replyToComment = useReplyToCommentQueryParam();
		const hasScrolledToFocusedComment = useRef(false);
		const hasScrolledToReplyComment = useRef(false);
		const {
			showInlineComments,
			showGeneralComments,
			replyToCommentId,
			commentType,
			currentlySelectedThreadKey,
			location: commentLocation,
		} = useCommentsPanelState();

		const { setCurrentlySelectedThreadKey, setReplyToCommentId } = useCommentsPanelActions();
		const [{ isUnreadQueryLoading }] = useUnreadComments();
		const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
		const experienceTracker = useContext(ExperienceTrackerContext);

		useEffect(() => {
			if (!isUnreadQueryLoading && !hasLoadedOnce) {
				setHasLoadedOnce(true);
			}
		}, [isUnreadQueryLoading, hasLoadedOnce]);

		// Set up IntersectionObserver
		useEffect(() => {
			if (isServer) {
				return;
			}

			// eslint-disable-next-line no-restricted-syntax
			const observer = new IntersectionObserver(
				([entry]) => {
					const isIntersecting = entry.isIntersecting;
					if (isIntersecting) {
						setIsInViewport(isIntersecting);
					}
				},
				{
					root: null,
					rootMargin: '10px',
					threshold: 0.1,
				},
			);

			if (wrapperRef.current) {
				observer.observe(wrapperRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}, [isServer, pageMode, state.isContentReady]);

		// Add MutationObserver to detect when comments are actually rendered
		useEffect(() => {
			if (isServer) {
				return;
			}

			const observer = new MutationObserver(() => {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const commentElement = document.getElementById(commentElementId);

				if (commentElement) {
					setAllCommentsRendered(true);
					observer.disconnect(); // Disconnect once we find the element
				} else {
					setAllCommentsRendered(false); // Reset if element not found
				}
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});

			// Reset allCommentsRendered when unmounting or changing thread key
			return () => {
				setAllCommentsRendered(false);
				observer.disconnect();
			};
		}, [currentlySelectedThreadKey, isServer]);

		// Query setup - skip if not forcing load and not in viewport and not loaded yet
		const commentsQueryHook = useActiveCommentsQuery;
		const skipQuery = !forceLoadComments && !isInViewport;
		const commentsQueryHookVariables: CommentsQueryHookVariables = {
			pageId: contentId || '',
			skip: skipQuery,
			type: CommentsType.FOOTER,
		};

		const { loading, error, data } = commentsQueryHook(commentsQueryHookVariables);

		const noOpenObjectComments = useMemo(() => {
			return data?.comments?.nodes?.length === 0 || true;
		}, [data]);

		// Track the start of comments loading and initialize performance metrics
		useEffect(() => {
			if (loading && fg('confluence_frontend_view_page_comments_exp_new')) {
				// Start tracking the page comments experience with initial attributes
				experienceTracker.start({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						isConditionalRenderingOn: true,
						isFocusedComment: Boolean(focusedCommentId || replyToComment),
						contentId,
						pageMode,
					},
				});

				// Initialize and mark the start of page comments rendering metrics
				PAGE_COMMENTS_METRIC.start();
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderStart');
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [loading]);

		// Track any errors that occur during comments loading
		useEffect(() => {
			if (error && fg('confluence_frontend_view_page_comments_exp_new')) {
				// Mark the experience as failed and include the error details
				experienceTracker.fail({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					error,
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [error]);

		// Track successful completion of comments rendering
		useEffect(() => {
			if (allCommentsRendered && fg('confluence_frontend_view_page_comments_exp_new')) {
				// Mark the experience as successful and include total comments count
				experienceTracker.succeed({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						totalComments: data?.comments?.totalCount,
					},
				});

				// Mark key performance metrics:
				// - fmp: First Meaningful Paint
				// - tti: Time to Interactive
				// - pageCommentsRenderEnd: End of comments rendering
				PAGE_COMMENTS_METRIC.mark('fmp');
				PAGE_COMMENTS_METRIC.mark('tti');
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderEnd');
				// Stop the metric tracking and include final data
				PAGE_COMMENTS_METRIC.stop({
					customData: {
						contentId: contentId || '',
						totalComments: data?.comments?.totalCount || 0,
					},
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [allCommentsRendered]);

		// Handle case when there are no comments to render
		useEffect(() => {
			if (
				data?.comments?.totalCount === 0 &&
				fg('confluence_frontend_view_page_comments_exp_new')
			) {
				// Abort the experience tracking since there are no comments to render
				experienceTracker.abort({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					reason: 'No page comments',
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [data]);

		// Update loaded state when data is received
		useEffect(() => {
			if (data && !hasLoaded) {
				setHasLoaded(true);
				setForceLoadComments(false);
			}
		}, [data, hasLoaded]);

		// Handle scrolling after comments are loaded
		useEffect(() => {
			if (hasLoaded && commentType === CommentType.GENERAL && allCommentsRendered) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (isCommentExists) {
					const rect = isCommentExists.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);
					if (!commentIsInViewport) {
						isCommentExists.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
							inline: 'center',
						});
					}
				}
			}
		}, [hasLoaded, commentType, currentlySelectedThreadKey, allCommentsRendered]);

		useEffect(() => {
			if (commentType === CommentType.GENERAL) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (!isCommentExists) {
					setForceLoadComments(true);
				}
			}
		}, [currentlySelectedThreadKey, commentType]);

		// Handle editor resize
		useEffect(() => {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			if (editorTextArea) {
				const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
					if (fg('confluence_footer_comment_styling_change')) {
						const newTop = getCommentPositionTopOfSection(entries);
						if (fg('confluence_footer_comment_styling_fix_flicker')) {
							flushSync(() => {
								newTop && setCommentSectionTop(newTop);
							});
						} else {
							newTop && setCommentSectionTop(newTop);
						}
					}
				});
				resizeObserver.observe(editorTextArea);
				return () => resizeObserver.disconnect();
			}
		}, []);

		// Handle scrolling to focused comment when data is loaded
		useEffect(() => {
			if (
				hasLoaded &&
				(focusedCommentId || replyToComment) &&
				allCommentsRendered &&
				!hasScrolledToFocusedComment.current &&
				!hasScrolledToReplyComment.current &&
				!fg('confluence_frontend_resolved_page_comment_links')
			) {
				const commentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-container`;
				const replyCommentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-replies`;

				const commentElement = document.getElementById(commentElementId);
				const replyCommentElement = document.getElementById(replyCommentElementId);
				const element = commentElement || replyCommentElement;
				if (element) {
					if (replyToComment) {
						// Grab the comment object so we can get their user info
						const replyToCommentObject = findCommentById(commentsDataMap, replyToComment);
						const topLevelParentComment = getTopLevelParentComment(commentsDataMap, replyToComment);

						// If nested comments aren't allowed, make sure we're replying to the top level parent
						const commentToReplyTo = hasNestedComments
							? replyToCommentObject
							: topLevelParentComment;

						// No matter what, mention the intended user
						const replyToUser = {
							// This field controls who we auto mention in the reply
							displayName: replyToCommentObject?.author?.displayName,
							accountId: (replyToCommentObject?.author as InlineCommentAuthorUser)?.accountId,
						};
						// Thread key should be handled by the top level parent
						setCurrentlySelectedThreadKey(topLevelParentComment?.id ?? replyToComment);

						setReplyToCommentId({
							parentCommentId: commentToReplyTo?.id,
							location: LocationValue.FOOTER,
							user: replyToUser,
						});
						hasScrolledToReplyComment.current = true;
						return;
					}
					const rect = element.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);
					if (!commentIsInViewport) {
						element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
						hasScrolledToFocusedComment.current = true;
						setCurrentlySelectedThreadKey(focusedCommentId);
						// If the document scrolls to a focused comment on View or Live Page, the VC event is aborted.
						if (fg('confluence_frontend_abort_vc_on_auto_scroll')) {
							abortAll('new_interaction', `document-scroll:object-comment-container`);
						}
					}
				}
			}
		}, [
			hasLoaded,
			focusedCommentId,
			replyToComment,
			allCommentsRendered,
			setCurrentlySelectedThreadKey,
			setReplyToCommentId,
			hasNestedComments,
			commentsDataMap,
		]);

		// Reset scroll tracking when focusedCommentId or replyToComment changes
		useEffect(() => {
			if (!fg('confluence_frontend_resolved_page_comment_links')) {
				hasScrolledToFocusedComment.current = false;
				hasScrolledToReplyComment.current = false;
			}
		}, [focusedCommentId, replyToComment]);

		// Force load comments if focusedCommentId is present
		useEffect(() => {
			if (
				(focusedCommentId || replyToComment) &&
				!hasLoaded &&
				!fg('confluence_frontend_resolved_page_comment_links')
			) {
				setForceLoadComments(true);
			}
		}, [focusedCommentId, replyToComment, hasLoaded]);

		const commentThreads = useMemo(() => {
			if (!hasLoaded) {
				return [];
			}

			return fg('confluence_frontend_immediate_comment_removal')
				? getVisibleCommentThreads({
						commentsDataMap,
						includeGeneralComments: true,
						includeInlineComments: false,
						orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
					})
				: getOpenCommentThreads({
						commentsDataMap,
						showInlineComments,
						showGeneralComments,
						location: LocationValue.FOOTER,
						orderedActiveAnnotationIdList,
						removedThreadsMap,
					});
		}, [
			hasLoaded,
			commentsDataMap,
			orderedActiveAnnotationIdList,
			removedThreadsMap,
			showInlineComments,
			showGeneralComments,
		]);

		const isOffline = useAnnotationsIsOffline();

		const getMaxWidth = () => {
			if (pageWidthType === undefined) {
				return '100%';
			}
			// pageWidthType is a string like '"default"'
			return JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT ? '1000px' : 'unset';
		};

		const hasComments = commentThreads.length > 0;

		// Show placeholder when:
		// For View mode: show when content is ready and comments are not loaded
		// For Live mode: show when in viewport and loading
		// Always show during SSR
		const shouldShowPlaceholder =
			pageMode === PageMode.VIEW ? state.isContentReady && !hasLoaded : isInViewport && loading;

		// Show comments when loaded, regardless of viewport
		const shouldShowComments = hasLoaded;

		return (
			<Box
				ref={wrapperRef}
				xcss={wrapperStyle}
				testId="object-comment-wrapper"
				style={{
					top: commentSectionTop,
					maxWidth: getMaxWidth(),
					// display: 'contents', // So wrapper doesn't affect layout if empty
				}}
			>
				{fg('confluence_frontend_resolved_page_comment_links') && (
					<ObjectCommentQueryParamsListener
						hasLoadedObjectComments={hasLoaded || skipQuery}
						allObjectCommentsRendered={allCommentsRendered || noOpenObjectComments}
						setForceLoadComments={setForceLoadComments}
					/>
				)}
				{error ||
				(pageMode === PageMode.VIEW && !state.isContentReady) ||
				(isUnreadQueryLoading && !hasLoadedOnce) ? null : (
					<>
						{shouldShowPlaceholder && (
							<Box xcss={placeholderStyle}>
								<CommentPlaceholder showPlaceholder />
							</Box>
						)}
						{shouldShowComments && (
							<ReactionsProvider contentId={contentId ?? ''}>
								{hasComments && <ObjectCommentHeader isOffline={isOffline} />}
								<Stack xcss={commentPanelContainerStyles}>
									<ObjectCommentsList
										commentThreads={commentThreads}
										supportedTopLevelActions={['edit', 'resolve', 'delete']}
										flags={flags}
										onAllCommentsRendered={() => setAllCommentsRendered(true)}
										isOffline={isOffline}
									/>
								</Stack>
								<Box
									xcss={[
										addCommentContainerStyles,
										!!replyToCommentId && commentLocation === LocationValue.FOOTER
											? addCommentContainerAnimationHiddenStyles
											: addCommentContainerAnimationShowStyles,
									]}
								>
									{!shouldShowPlaceholder && (
										<ObjectAddCommentContainer showReactions={!hasComments} isOffline={isOffline} />
									)}
								</Box>
							</ReactionsProvider>
						)}
					</>
				)}
			</Box>
		);
	},
);
